import React, { type FunctionComponent, useMemo, useState, useCallback, useEffect } from 'react';
import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import { PRODUCT_TUTORIALS } from 'common/constants';
import { Information } from 'components/Information/Information';
import { trackEvent } from '@surfline/web-common';

import styles from './ObservedDataToggle.module.scss';

interface Props {
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  spotId?: string;
  spotName?: string;
  subregionId?: string;
  subregionName?: string;
}

const ObservedDataToggle: FunctionComponent<Props> = ({
  defaultChecked = false,
  onChange,
  spotId,
  spotName,
  subregionId,
  subregionName,
}) => {
  const [isChecked, setIsChecked] = useState<boolean>(defaultChecked);

  // Update internal state when defaultChecked changes
  useEffect(() => {
    setIsChecked(defaultChecked);
  }, [defaultChecked]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      setIsChecked(checked);
      onChange?.(checked);
      trackEvent('Clicked Toggle', {
        pageName: 'Spot Report',
        spotId,
        spotName,
        subregionId,
        subregionName,
        toggleEnd: checked ? 'on' : 'off',
        toggleStart: !checked ? 'on' : 'off',
        toggleType: 'observed surf heights',
      });
    },
    [onChange, spotId, spotName, subregionId, subregionName],
  );

  const informationSegmentProps = useMemo(
    () => ({
      modalType: 'tutorial',
      spotId,
      spotName,
      subregionId,
      subregionName,
      tabName: 'report',
      uiElement: 'lotus-information',
    }),
    [spotId, spotName, subregionId, subregionName],
  );

  return (
    <Box className={styles.wrapper}>
      <FormControlLabel
        control={
          <Switch
            checked={isChecked}
            onChange={handleChange}
            size="small"
            data-testid="observed-data-toggle"
          />
        }
        label={
          <Box className={styles.labelContent} component="span">
            <span className={styles.desktopLabel}>
              <Typography variant="footnote">Show observed surf heights</Typography>
            </span>
            <span className={styles.mobileLabel}>
              <Typography variant="footnote">Observed heights</Typography>
            </span>
          </Box>
        }
      />
      <Information
        articleId={PRODUCT_TUTORIALS.articles.surfHeight.id}
        segmentProps={informationSegmentProps}
        tooltip={PRODUCT_TUTORIALS.articles.surfHeight.tooltip}
        tutorialId={PRODUCT_TUTORIALS.tutorials.default.id}
      />
    </Box>
  );
};

export default ObservedDataToggle;
